import { Box } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * A shape, for numbers that are genuinely a sequence.
 *
 * ## The one chart in this product was the best thing in it
 *
 * Agent OS's attended/unattended bar is the only piece of data visualisation
 * anywhere here, and it is the thing on the screen that reads as designed.
 * That is a signal rather than a coincidence: a proportion is something you
 * *see* and a column of counts is something you scan, and this product is
 * full of counts that are secretly proportions and sequences.
 *
 * ## What it refuses to draw
 *
 * **Only things that are actually a sequence.** `2,316 records in the graph`
 * is a count of what exists now, and drawing it as a line would invent a
 * history the kernel does not have — the same overstatement this product
 * exists to prevent, in a new medium. Writes per day out of the audit log is
 * a sequence. Written-against-rejected per run is a sequence. A total is not.
 *
 * **Nothing it cannot label.** Every bar carries its own value and its own
 * name in the tooltip, and the whole thing carries an accessible label, so a
 * reader who cannot see the shape gets the numbers. A chart that is the only
 * way to read a fact is a fact half the audience does not have.
 *
 * **No axis it did not measure.** There is no y-scale printed, because the
 * bars are relative to the largest value in the set and saying so in a
 * caption is honest where a fake axis would not be. `max` can be passed when
 * the caller knows a real ceiling.
 *
 * ## Drawn, not imported
 *
 * Fifty lines of SVG against a charting library that is a hundred kilobytes
 * and brings its own opinions about tooltips, fonts and colour. The
 * components in this package are the product's argument; a chart library
 * would make a third of a screen somebody else's argument.
 */
export default function Sparkline({
  values = [],
  labels = [],
  max,
  height = 32,
  gap = 2,
  tone = "primary.main",
  label,
}) {
  const numbers = values.map((v) => Number(v) || 0);
  if (!numbers.length) return null;

  const ceiling = Math.max(max || 0, ...numbers, 1);

  return (
    <Box
      role="img"
      aria-label={
        label
          || `A bar for each of ${numbers.length} values, the tallest being ${ceiling}.`
      }
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: `${gap}px`,
        height,
        minWidth: 0,
      }}
    >
      {numbers.map((value, index) => (
        <Tooltip
          key={index}
          title={
            labels[index]
              ? `${labels[index]} — ${value.toLocaleString()}`
              : value.toLocaleString()
          }
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 2,
              // A zero is drawn as a hairline rather than as nothing. An
              // empty slot and a day with no writes look identical if a zero
              // renders as absence, and they are different facts — one is
              // "nothing happened" and the other is "we have no reading".
              height: `${Math.max((value / ceiling) * 100, value === 0 ? 2 : 6)}%`,
              borderRadius: 0.5,
              backgroundColor: value === 0 ? "divider" : tone,
              transition: "height 180ms ease-out",
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              "&:hover": { opacity: 0.75 },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

/**
 * The proportion bar, promoted out of Agent OS.
 *
 * It was written for the Attended screen and it is the shape any
 * two-or-three-way split wants: how much of this is one thing rather than
 * another, seen rather than counted.
 *
 * `segments` is `[{ value, colour, label, detail }]`. Colour is never the only
 * signal — the caller renders the labelled chips underneath — and this draws
 * only the shape.
 */
export function Proportion({ segments = [], total, height = 10 }) {
  const sum = total || segments.reduce((n, s) => n + (Number(s.value) || 0), 0);
  if (!sum) return null;

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height,
        borderRadius: 999,
        overflow: "hidden",
        backgroundColor: "action.hover",
      }}
    >
      {segments.map((segment) =>
        segment.value ? (
          <Tooltip
            key={segment.label}
            title={`${segment.value.toLocaleString()} — ${segment.detail || segment.label}`}
          >
            <Box
              sx={{
                width: `${(segment.value / sum) * 100}%`,
                backgroundColor: segment.colour,
                transition: "width 220ms ease-out",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            />
          </Tooltip>
        ) : null,
      )}
    </Box>
  );
}
